import {DataStatus, EmptyStatus} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {
  InterestRecommendedVehicleResponseBody,
  useGetOfferedVehiclesListQuery,
} from '@dms/api/finance';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {RecommendedVehicle} from './RecommendedVehicle';

interface RecommendedVehiclesProps extends RequiredTestIdProps {
  interestId: string;
  recommendations: InterestRecommendedVehicleResponseBody[];
}

export function RecommendedVehicles(props: RecommendedVehiclesProps) {
  // currentData is empty while the vehicles of new recommendations load, so the previous list never
  // pairs with the new recommendations
  const {
    currentData: vehicles,
    isFetching,
    isError,
  } = useGetOfferedVehiclesListQuery({
    body: {vehicleIds: props.recommendations.map((recommendation) => recommendation.vehicleId)},
  });

  // Keeps the ranking of the recommendations and pairs each one with its vehicle detail
  const recommendedVehicles = props.recommendations.flatMap((recommendation) => {
    const vehicle = vehicles?.find((item) => item.id === recommendation.vehicleId);
    return isNil(vehicle) ? [] : [{recommendation, vehicle}];
  });

  // The vehicle list can miss a recommended vehicle, for example one sold after the recommendations loaded
  if (isNotNil(vehicles) && isEmpty(recommendedVehicles)) {
    return (
      <EmptyStatus
        variant="vehicle"
        headline={i18n.t('entity.vehicle.labels.noMatchingVehicles')}
        data-testid={suffixTestId('noMatch', props)}
      />
    );
  }

  return (
    <DataStatus isLoading={isFetching && isNil(vehicles)} isError={isError} minHeight={25}>
      <VStack spacing={2}>
        {recommendedVehicles.map(({recommendation, vehicle}, index) => (
          <RecommendedVehicle
            key={vehicle.id}
            interestId={props.interestId}
            vehicle={vehicle}
            recommendation={recommendation}
            data-testid={suffixTestId(`vehicle-[${index}]`, props)}
          />
        ))}
      </VStack>
    </DataStatus>
  );
}
